import Vue from 'vue'
import axios from 'axios'
import router from '@/router'
import store from './store'

const AUTH_ENDPOINT = '/admin/login'
const USER_LIST_ENDPOINT = '/admin/users/individual'
const USER_STATS_ENDPOINT = '/admin/users/stats'
const USER_LIST_SINGLE_ENDPOINT = '/admin/users/individual/'
const ADMIN_INFO_ENDPOINT = '/admin'
const ADMIN_INFO_UPDATE_ENDPOINT = '/admin'

const snakeToCamel = (str) => str.replace(
	/([-_][a-z])/g,
	(group) => group.toUpperCase()
		.replace('-', '')
		.replace('_', '')
)

class SolFileAPI {

	constructor() {
		this.createInstance()
	}

	createInstance = () => {
		this.instance = axios.create({
			baseURL: process.env.VUE_APP_API_ROOT,
			headers: {
				'Authorization': this.token ? this.token : `Bearer ${localStorage.getItem('token')}`,
				'Content-Type': 'text/plain',
				Accept: '*/*'
			},
			withCredentials: false
		})
	}

	setToken = token => {
		localStorage.setItem('token', token);
		this.token = `Bearer ${token}`
		this.createInstance()
	}

	authenticate = async data => {
		try {
			const response = await axios.post(
				`${process.env.VUE_APP_API_ROOT}${AUTH_ENDPOINT}`,
				data, {
					headers: {
						Accept: '*/*',
						'Content-Type': 'text/plain'
					}
				})
			this.setToken(response.data.data.token)
			return response
		} catch (e) {
			this.handleError(e)
			return e.response
		}
	}


	getUserById = async id => {
		try {
			return await this.instance.get(`${USER_LIST_SINGLE_ENDPOINT}${id}`)
		} catch (e) {
			this.handleError(e.response.data.message, true)
		}
	}

	getUsers = async params => {
		try {
			return await this.instance.get(USER_LIST_ENDPOINT, { params })
		} catch (e) {
			this.handleError(e)
		}
	}

	getUserStats = async () => {
		try {
			return await this.instance.get(USER_STATS_ENDPOINT)
		} catch (e) {
			this.handleError(e)
		}
	}


	setUserInfoById = async (id, data) => {
		try {
			const response = await this.instance.patch(`${USER_LIST_SINGLE_ENDPOINT}${id}`, data)
			this.handleSuccessMessage(true, '/individuals')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	getFootprintKeysList = async () => {
		try {
			return await this.instance.get('admin/filters/footprint')
		} catch (e) {
			this.handleError(e);
		}
	}

	getFootprintList = async params => {
		try {
			return await this.instance.get('admin/footprint', {
				params
			})
		} catch (e) {
			this.handleError(e);
		}
	}

	getFootprintById = async id => {
		try {
			return await this.instance.get(`admin/footprint/${id}`)
		} catch (e) {
			this.handleError(e)
		}
	}

	addNewFootprint = async data => {
		try {
			const response = await this.instance.post('admin/footprint', data)
			this.handleSuccessMessage(true, '/footprint', 'Footprint successfully added')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	updateFootprintById = async data => {
		try {
			const {
				id,
				...rest
			} = data
			const response = await this.instance.patch(`admin/footprint/${id}`, rest)
			this.handleSuccessMessage(true, '/footprint', 'Footprint successfully updated')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	deleteFootprintById = async id => {
		try {
			const response = await this.instance.delete(`admin/footprint/${id}`)
			this.handleSuccessMessage(true, '/footprint', 'Footprint successfully deleted')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	getAdminInfo = async () => {
		try {
			return await this.instance.get(ADMIN_INFO_ENDPOINT)
		} catch (e) {
			this.handleError(e)
		}
	}

	setAdminInfo = async data => {
		try {
			const response = await this.instance.patch(ADMIN_INFO_UPDATE_ENDPOINT, data)
			this.handleSuccessMessage()
			return response
		} catch (e) {
			this.handleError(e)
			return e
		}
	}

	deleteUserById = async id => {
		try {
			const response = await this.instance.delete(`${USER_LIST_SINGLE_ENDPOINT}${id}`)
			this.handleSuccessMessage(true, '/', 'User successfully deleted')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	getCountryListWithParams = async countryName => {
		try {
			return await this.instance.get('/countries/name', {
				params: {
					countryName
				}
			})
		} catch (e) {
			this.handleError(e)
		}
	}

	getCityListWithParams = async params => {
		try {
			return await this.instance.get('/locations/name', {
				params
			})
		} catch (e) {
			this.handleError(e)
		}
	}

	getRetrogrades = async () => {
		try {
			return await this.instance.get('/admin/retrogrades')
		} catch (e) {
			this.handleError(e)
		}
	}

	saveRetrogrades = async data => {
		try {
			const transformedData = data.map(item => {
				const {
					Question,
					Answer
				} = item
				return {
					Question,
					Answer
				}
			})
			const response = await this.instance.patch('/admin/retrogrades', {
				retrogrades: transformedData
			})
			this.handleSuccessMessage(false, '', 'Retrogrades successfully updated')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	getDiscoveryFilters = async () => {
		try {
			return await this.instance.get('/admin/filters/discovery')
		} catch (e) {
			this.handleError(e)
		}
	}

	getDiscoveries = async data => {
		const {
			sortingParams,
			...params
		} = data
		const filterParams = sortingParams.reduce((acc, item) => {
			const {type, value} = item
			if (value) {
				acc[snakeToCamel(type)] = value
			}
			return acc
		}, {})
		try {
			return await this.instance.get('/discovery', {
				params: {
					...params,
					...(filterParams && {...filterParams})
				}
			})
		} catch (e) {
			this.handleError(e)
		}
	}

	getDiscoveryById = async id => {
		try {
			return await this.instance.get(`/discovery/${id}`)
		} catch (e) {
			this.handleError(e)
		}
	}

	deleteDiscoveryInterpretationById = async id => {
		try {
			const response = await this.instance.delete(`/discovery/${id}`)
			this.handleSuccessMessage(true, '/discovery', 'Discovery Interpretation successfully deleted')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	saveNewDiscoveryInterpretation = async data => {
		try {
			const response = await this.instance.post('/discovery', data)
			this.handleSuccessMessage(true, '/discovery', 'Discovery Interpretation successfully added')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	editDiscoveryInterpretationById = async (id, data) => {
		try {
			const response =  await this.instance.patch(`/discovery/${id}`, data)
			this.handleSuccessMessage(false, '', 'Discovery Interpretation successfully updated')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	getAspectFilters = async () => {
		try {
			return await this.instance.get('admin/filters/aspects')
		} catch (e) {
			this.handleError(e)
		}
	}

	getAspectInterpretations = async params => {
		const {
			sortingParams,
			...rest
		} = params
		const transformedData = sortingParams.reduce((accumulator, item) => {
			if (item.value) {
				//weep-weep
				accumulator[snakeToCamel(item.type) === 'aspects' ? 'aspect' : snakeToCamel(item.type)] = item.value
			}
			return accumulator
		}, {})
		try {
			return await this.instance.get('admin/aspects', {
				params: {
					...rest,
					...(Object.values(transformedData) && transformedData)
				}
			})
		} catch (e) {
			this.handleError(e)
		}
	}

	addNewAspectInterpretation = async data => {
		try {
			const response = await this.instance.post('admin/aspects', data)
			this.handleSuccessMessage(true, '/aspect', 'Aspect Interpretation successfully created')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	updateAspectInterpretation = async data => {
		try {
			const {
				id,
				...rest
			} = data
			const response = await this.instance.patch(`admin/aspects/${id}`, rest)
			this.handleSuccessMessage(true, '/aspect', 'Aspect Interpretation successfully updated')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	getDiscoveryDropdownValues = async () => {
		try {
			return await this.instance.get('admin/dropdowns/discovery')
		} catch (e) {
			this.handleError(e, true)
		}
	}

	deleteAspectInterpretationById = async id => {
		try {
			const response = await this.instance.delete(`admin/aspects/${id}`)
			this.handleSuccessMessage(true, '/aspect', 'Aspect Interpretation successfully deleted')
			return response
		} catch (e) {
			this.handleError(e)
		}
	}

	getAspectInterpretationById = async id => {
		try {
			return  await this.instance.get(`admin/aspects/${id}`)
		} catch (e) {
			this.handleError(e)
		}
	}

	handleError = (e, redirect) => {
		if (e.response && e.response.status && 401 === e.response.status) {
			store.dispatch('logoutUser')
		}
		Vue.notify({
			group: 'error',
			type: 'error',
			title: 'Error occurred',
			text: e.response.data.message ? e.response.data.message : 'Error occurred, please try again later'
		})
		redirect && router.push({ path: '/' })
	}

	handleSuccessMessage = (redirect, redirectURL = '/', e = 'Successfully updated') => {
		Vue.notify({
			group: 'error',
			type: 'success',
			text: e
		})
		redirect && router.push({ path: redirectURL })
	}

	handleExportUsers = async ({from, to}) => {
		try {
			const response = await this.instance.get(`export/admin/users/individual?from=${from}&to=${to}`);
			this.handleSuccessMessage(false, '', 'You will receive an email with the Export file once the generation is complete.')
			return response.data;
		} catch (e) {
			this.handleError(e)
		}
	}
	handleGetIndividualUserSubscription = async (userId) => {
		try {
			const response = await this.instance.get(`${USER_LIST_SINGLE_ENDPOINT}/${userId}/subscription`);
			return response.data;
		} catch(e){
			this.handleError(e);
		}
	}
	handleGetNewUserToken = async (data) => {
		try{ 
			const response = await this.instance.post('user/registration', data)
			return response.data
		} catch(e) {
			this.handleError(e)
		}
	}
	handleCreateUser = async (token, data) => {
		try{ 
			const response = await axios.patch(
				`${process.env.VUE_APP_API_ROOT}/user`,
				data,
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'text/plain',
						Accept: '*/*'
					},
					withCredentials: false
				},
			)
			this.handleSuccessMessage(true, '/individuals', 'Successfully Created')
			return response
		} catch(e) {
			this.handleError(e)
		}
	}

}

const SolFileAPIInstance = new SolFileAPI()

export default SolFileAPIInstance
